import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Search } from "lucide-react";
import { useState } from "react";
import { BLOG_ARTICLES, BLOG_CATEGORIES } from "@/lib/blogData";
import PageHero from "@/components/PageHero";
import ResponsiveImage from "@/components/ResponsiveImage";
import SEO from "@/components/SEO";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const Blog = () => {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = BLOG_ARTICLES.filter((a) => {
    if (filter !== "all" && a.category !== filter) return false;
    if (
      search.length > 1 &&
      !`${a.title} ${a.excerpt} ${a.keywords}`
        .toLowerCase()
        .includes(search.toLowerCase())
    )
      return false;
    return true;
  });

  // Sorted newest first
  const sorted = [...filtered].sort((a, b) =>
    a.date < b.date ? 1 : -1
  );

  const blogJsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Conseils propriétaires, Terre et Mer Conciergerie",
    description:
      "Tous nos conseils pour propriétaires en location courte durée Airbnb sur la Côte d'Émeraude et le Morbihan : rentabilité, réglementation, photos, pricing dynamique, fiscalité LMNP.",
    url: "https://terreetmer-conciergerie.fr/blog",
    blogPost: BLOG_ARTICLES.map((a) => ({
      "@type": "BlogPosting",
      headline: a.title,
      datePublished: a.date,
      author: { "@type": "Organization", name: "Terre et Mer Conciergerie" },
      url: `https://terreetmer-conciergerie.fr/blog/${a.slug}`,
      image: `https://terreetmer-conciergerie.fr${a.image}`,
    })),
  };

  return (
    <div data-testid="page-blog">
      <SEO
        title="Conseils propriétaires Airbnb Bretagne, Blog Terre et Mer"
        description="Tous nos conseils pour propriétaires Airbnb sur la Côte d'Émeraude et le Morbihan : Saint-Malo, Dinard, Cancale, Damgan, La Roche-Bernard. Rentabilité, photos, pricing, fiscalité LMNP."
        keywords="blog airbnb bretagne, conseils propriétaires airbnb saint-malo, location courte durée bretagne conseils, rentabilité airbnb dinard, lmnp airbnb 2026, pricing dynamique airbnb"
        jsonLd={blogJsonLd}
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Blog", path: "/blog" },
        ]}
      />

      <PageHero
        overline="Conseils propriétaires"
        title="Le blog"
        italic="Terre et Mer"
        description="Rentabilité, fiscalité LMNP, optimisation d'annonce, pricing dynamique, réglementation 2026 : nos conseils concrets pour propriétaires Airbnb sur la Côte d'Émeraude et le Morbihan."
        image="/images/blog-hero-equipe-terre-et-mer.jpg"
      />

      {/* Filters & search */}
      <section className="bg-cream py-12 border-y border-gold/15" data-testid="blog-filters">
        <div className="container-tm">
          <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <FilterChip
                active={filter === "all"}
                onClick={() => setFilter("all")}
                label={`Tout (${BLOG_ARTICLES.length})`}
                testId="blog-filter-all"
              />
              {Object.entries(BLOG_CATEGORIES).map(([key, cat]) => {
                const count = BLOG_ARTICLES.filter((a) => a.category === key).length;
                if (!count) return null;
                return (
                  <FilterChip
                    key={key}
                    active={filter === key}
                    onClick={() => setFilter(key)}
                    label={`${cat.label} (${count})`}
                    testId={`blog-filter-${key}`}
                  />
                );
              })}
            </div>

            <div className="relative w-full lg:w-72">
              <Search size={16} strokeWidth={1.6} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/40" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher un article…"
                data-testid="blog-search"
                className="w-full pl-11 pr-4 py-2.5 rounded-full bg-white border border-ink/10 text-sm font-sans focus:outline-none focus:border-gold transition-colors"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Articles grid */}
      <section className="py-20 lg:py-28 bg-white" data-testid="blog-grid">
        <div className="container-tm">
          {sorted.length === 0 ? (
            <p className="text-center font-sans text-ink/60 py-20">
              Aucun article ne correspond à votre recherche.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {sorted.map((a) => (
                <ArticleCard key={a.slug} article={a} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-ink text-white py-20 lg:py-24" data-testid="blog-final-cta">
        <div className="container-tm text-center">
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl leading-tight mb-5">
            Prêt à <span className="italic text-gold">passer à l'action</span> ?
          </h2>
          <p className="font-sans text-white/70 max-w-xl mx-auto mb-8 leading-relaxed">
            Nos articles vous donnent la théorie. Notre équipe locale s'occupe de la mise en pratique. Demandez une étude gratuite et personnalisée pour votre bien.
          </p>
          <Link
            to="/contact"
            data-testid="blog-final-cta-button"
            className="btn-accent inline-flex items-center"
          >
            Demander une étude gratuite
            <ArrowRight size={14} strokeWidth={2} className="ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

const FilterChip = ({ active, onClick, label, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-5 py-2 rounded-full text-xs uppercase tracking-[0.18em] font-semibold transition-all ${
      active
        ? "bg-ink text-gold"
        : "bg-white text-ink/70 hover:bg-ink/5 border border-ink/10"
    }`}
  >
    {label}
  </button>
);

const ArticleCard = ({ article }) => {
  const cat = BLOG_CATEGORIES[article.category];
  return (
    <Link
      to={`/blog/${article.slug}`}
      data-testid={`blog-card-${article.slug}`}
      className="group bg-cream/30 hover:bg-white border border-ink/5 rounded-3xl overflow-hidden transition-all duration-500 hover:shadow-lift flex flex-col"
    >
      <div className="aspect-[16/10] overflow-hidden bg-cream">
        <ResponsiveImage
          src={article.image}
          alt={article.imageAlt || article.title}
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
      </div>
      <div className="p-7 flex flex-col flex-1">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-gold">
            {cat?.label || article.category}
          </span>
          <span className="text-ink/30">·</span>
          <span className="text-[10px] font-sans text-ink/50 inline-flex items-center gap-1.5">
            <Clock size={11} strokeWidth={1.8} />
            {article.readTime} min
          </span>
        </div>
        <h3 className="font-serif text-xl md:text-2xl text-ink leading-tight mb-3">
          {article.title}
        </h3>
        <p className="font-sans text-sm text-ink/60 leading-relaxed line-clamp-3 mb-5 flex-1">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between border-t border-gold/15 pt-4 mt-auto">
          <span className="text-[11px] font-sans text-ink/45 inline-flex items-center gap-1.5">
            <Calendar size={11} strokeWidth={1.8} />
            {formatDate(article.date)}
          </span>
          <span className="inline-flex items-center gap-2 text-sage font-sans text-[11px] uppercase tracking-[0.22em] font-semibold group-hover:gap-3 transition-all">
            Lire
            <ArrowRight size={12} strokeWidth={2} />
          </span>
        </div>
      </div>
    </Link>
  );
};

export default Blog;
